import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Sparkles, Copy, Check, ExternalLink, ArrowLeft, RefreshCw, BookOpen, Zap, Star } from 'lucide-react';
import { toast } from 'sonner';
import ToolRecommendations from '../components/ToolRecommendations';
import GettingStarted from '../components/GettingStarted';
import RelatedTools from '../components/RelatedTools';

export default function ResultsPage({ userAnswers, generatedPrompt, onGenerateNew, onBackToHome }) {
  const [copied, setCopied] = useState(false);
  
  console.log('ResultsPage render - copied state:', copied);

  const handleCopyPrompt = async () => {
    try {
      // Set copied state immediately for visual feedback
      console.log('Setting copied to true');
      setCopied(true);
      
      // Try modern clipboard API first
      if (navigator.clipboard && navigator.clipboard.writeText) {
        try {
          await navigator.clipboard.writeText(generatedPrompt);
          toast.success('✅ Prompt copied to clipboard!');
          setTimeout(() => {
            console.log('Setting copied to false');
            setCopied(false);
          }, 2000);
          return;
        } catch (clipboardErr) {
          console.warn('Clipboard API failed, trying fallback:', clipboardErr);
          // Don't reset state here, let fallback handle it
        }
      }
      
      // Fallback method - more reliable
      const textArea = document.createElement('textarea');
      textArea.value = generatedPrompt;
      
      // Make it invisible but selectable
      textArea.style.position = 'fixed';
      textArea.style.top = '0';
      textArea.style.left = '0';
      textArea.style.width = '2em';
      textArea.style.height = '2em';
      textArea.style.padding = '0';
      textArea.style.border = 'none';
      textArea.style.outline = 'none';
      textArea.style.boxShadow = 'none';
      textArea.style.background = 'transparent';
      textArea.style.opacity = '0';
      
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      
      let successful = false;
      try {
        successful = document.execCommand('copy');
      } catch (err) {
        console.error('execCommand failed:', err);
      }
      
      document.body.removeChild(textArea);
      
      if (successful) {
        toast.success('✅ Prompt copied to clipboard!');
        setTimeout(() => {
          console.log('Setting copied to false (fallback)');
          setCopied(false);
        }, 2000);
      } else {
        // Last resort - select the text in the prompt box
        const promptElement = document.querySelector('pre');
        if (promptElement) {
          const range = document.createRange();
          range.selectNode(promptElement);
          window.getSelection().removeAllRanges();
          window.getSelection().addRange(range);
          toast.info('📋 Text selected! Press Ctrl+C (Cmd+C on Mac) to copy');
        } else {
          toast.error('❌ Please manually select and copy the text above');
        }
      }
    } catch (err) {
      console.error('All copy methods failed:', err);
      setCopied(false); // Reset state on error
      toast.error('❌ Please manually select and copy the text above');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-muted/20">
      {/* Header */}
      <nav className="border-b bg-card/80 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <button onClick={onBackToHome} className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors">
              <ArrowLeft className="w-4 h-4" />
              <span className="hidden sm:inline">Back to Home</span>
            </button>
            <div className="flex items-center gap-2">
              <img 
                src="/growthyfai-logo.png" 
                alt="Growthyfai" 
                className="h-10 w-auto"
              />
              <div className="flex flex-col">
                <span className="text-lg font-bold leading-tight">PromptCraft AI</span>
                <span className="text-xs text-muted-foreground">by Growthyfai</span>
              </div>
            </div>
            <Button onClick={onGenerateNew} variant="outline" size="sm" className="gap-2">
              <RefreshCw className="w-4 h-4" />
              <span className="hidden sm:inline">New Prompt</span>
            </Button>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 lg:py-12">
        {/* Success Banner */}
        <div className="mb-8 animate-fade-in">
          <Card className="border-2 border-success/20 bg-success/5">
            <CardContent className="pt-6 pb-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-success flex items-center justify-center flex-shrink-0">
                  <Check className="w-6 h-6 text-success-foreground" />
                </div>
                <div>
                  <h3 className="font-semibold text-lg">Your AI-Optimized Smart Prompt is Ready!</h3>
                  <p className="text-muted-foreground">Copy the professionally formatted prompt below and paste it into any AI tool (ChatGPT, Claude, Gemini, etc.)</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Your Details Summary */}
        <Card className="mb-8 animate-slide-up">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <BookOpen className="w-5 h-5" />
              Your Details
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              <div>
                <span className="text-sm text-muted-foreground block mb-1">Use Case</span>
                <Badge variant="secondary" className="font-normal">{userAnswers.useCase}</Badge>
              </div>
              <div>
                <span className="text-sm text-muted-foreground block mb-1">Experience Level</span>
                <Badge variant="secondary" className="font-normal">{userAnswers.experienceLevel}</Badge>
              </div>
              <div>
                <span className="text-sm text-muted-foreground block mb-1">Tone</span>
                <Badge variant="secondary" className="font-normal">{userAnswers.tone}</Badge>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Generated Prompt */}
        <Card className="mb-8 animate-slide-up relative overflow-hidden" style={{ animationDelay: '0.1s' }}>
          {/* Watermark */}
          <div className="absolute top-4 right-4 opacity-10 pointer-events-none">
            <img 
              src="/growthyfai-logo.png" 
              alt="Growthyfai" 
              className="h-32 w-auto"
            />
          </div>
          
          <CardHeader>
            <div className="flex items-start justify-between gap-4">
              <div>
                <CardTitle className="flex items-center gap-2 text-2xl">
                  <Sparkles className="w-6 h-6 text-primary" />
                  Your AI-Optimized Smart Prompt
                </CardTitle>
                <CardDescription className="mt-2 flex items-center gap-2 flex-wrap">
                  <span>✨ Copy this prompt and paste it into ChatGPT, Claude, Gemini, or any AI tool!</span>
                  <Badge variant="outline" className="gap-1">
                    <img src="/growthyfai-logo.png" alt="Growthyfai" className="h-3 w-auto" />
                    Optimized by AI
                  </Badge>
                </CardDescription>
              </div>
              <Button 
                key={copied ? 'copied' : 'copy'} 
                onClick={handleCopyPrompt} 
                className="gap-2 flex-shrink-0"
                variant={copied ? 'default' : 'default'}
              >
                {copied ? (
                  <>
                    <Check className="w-4 h-4" />
                    Copied!
                  </>
                ) : (
                  <>
                    <Copy className="w-4 h-4" />
                    Copy
                  </>
                )}
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="bg-muted/50 rounded-lg p-6 border-2 border-border relative">
              <pre className="whitespace-pre-wrap font-sans text-sm leading-relaxed text-foreground">
                {generatedPrompt}
              </pre>
            </div>
            <div className="mt-4 space-y-2">
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Zap className="w-4 h-4" />
                <span>This prompt was professionally optimized and formatted by AI for maximum effectiveness</span>
              </div>
              <div className="flex items-center gap-2 text-xs text-muted-foreground border-t pt-3">
                <img src="/growthyfai-logo.png" alt="Growthyfai" className="h-4 w-auto" />
                <span className="font-medium">Professional prompt engineering by Growthyfai × OpenAI GPT-4</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Tabbed Content */}
        <div className="animate-slide-up" style={{ animationDelay: '0.2s' }}>
          <Tabs defaultValue="tools" className="space-y-6">
            <TabsList className="grid w-full grid-cols-3 h-auto">
              <TabsTrigger value="tools" className="py-3">Top Tools</TabsTrigger>
              <TabsTrigger value="guide" className="py-3">Getting Started</TabsTrigger>
              <TabsTrigger value="related" className="py-3">Related Tools</TabsTrigger>
            </TabsList>
            
            <TabsContent value="tools">
              <ToolRecommendations useCase={userAnswers.useCase} />
            </TabsContent>
            
            <TabsContent value="guide">
              <GettingStarted useCase={userAnswers.useCase} experienceLevel={userAnswers.experienceLevel} />
            </TabsContent>
            
            <TabsContent value="related">
              <RelatedTools useCase={userAnswers.useCase} />
            </TabsContent>
          </Tabs>
        </div>

        {/* CTA */}
        <div className="mt-12 text-center space-y-4">
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <Button onClick={onGenerateNew} size="lg" className="gap-2 w-full sm:w-auto">
              <RefreshCw className="w-5 h-5" />
              Generate Another Prompt
            </Button>
            <Button 
              size="lg" 
              variant="outline"
              onClick={() => window.open('https://growthyfai.notion.site/1000-Best-AI-Prompts-by-Growthyfai-17366cbde8e680e2b16ac5098cc8ada7?source=copy_link', '_blank')}
              className="gap-2 w-full sm:w-auto border-2 border-gold/30 hover:border-gold hover:bg-gold/5"
            >
              <Star className="w-5 h-5 text-gold" />
              Explore 1000+ Prompts
              <ExternalLink className="w-4 h-4" />
            </Button>
          </div>
          <p className="text-sm text-muted-foreground">
            Want more inspiration? Check out our curated collection of 1000+ proven AI prompts
          </p>
        </div>
      </div>
    </div>
  );
}
